import { Box, Button, ButtonGroup, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../app/hooks";
import { anyPermission, PermissionId } from "../../../../app/permissions";
import PrizaContainer from "../../../../features/base/grid/PrizaContainer";
import { isTokenReady } from "../../../../features/user/UserAvatar";
import { LoadState, worstState } from "../../../../redux/common";
import { SubjectPerformedInfo } from "../../../../redux/curriculum/model";
import { retrieveSubjectList, selectSubjectList, subjectListSlice } from "../../../../redux/curriculum/subjectListSlice";
import { retrieveSubjectPerformedList, selectSubjectPerformedList, subjectPerformedListSlice } from '../../../../redux/curriculum/subjectPerformedListSlice';
import { selectUser } from "../../../../redux/user/userSlice";
import { SubjectPerformedInsUpd } from "../../../curriculum/ins-upd/subject-performed/SubjectPerformedInsUpd";
import { SubjectPerformedDeleteModal } from "./SubjectPerformedDeleteModal";
import dayjs from 'dayjs';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PresenceList from "./PresenceList";

interface ContentListProps {
    schoolId: string;
    classroomId: string;
}

const SubjectPerformedList = ({ schoolId, classroomId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const user = useSelector(selectUser);
    const subjects = useSelector(selectSubjectList);
    const subjectPerformedList = useSelector(selectSubjectPerformedList);

    const [selectedSubjectPerformed, setSelectedSubjectPerformed] = useState(undefined as SubjectPerformedInfo | undefined);
    const [subjectPerformedToDelete, setSubjectPerformedToDelete] = useState(undefined as SubjectPerformedInfo | undefined);

    const canEdit = useMemo(
        () => anyPermission(
            user.priza.info, schoolId,
            [PermissionId.SCHOOL_ADMIN, PermissionId.SCHOOL_TEACHER]
        )
            || anyPermission(
                user.priza.info, classroomId,
                [PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER]
            ),
        [user, schoolId, classroomId]
    );

    useEffect(() => {
        if (subjects.schoolId && subjects.schoolId != schoolId) {
            dispatch(subjectListSlice.actions.clean());
        }
        if (subjects.state.state == LoadState.NotStarted && isTokenReady(user))
            dispatch(retrieveSubjectList({ schoolId }));
    }, [subjects, schoolId, user]);
    useEffect(() => {
        dispatch(subjectPerformedListSlice.actions.check({ classroomId }));
        if (subjectPerformedList.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSubjectPerformedList({ classroomId }));
        }
    }, [subjectPerformedList, classroomId, user]);

    const list = useMemo(() => {
        if (!subjects.list || !subjectPerformedList.list) {
            return [];
        }
        let l = [...subjectPerformedList.list];
        return l.sort((a, b) => {
            const aStart = a.startedAt ? dayjs(a.startedAt) : undefined;
            const bStart = b.startedAt ? dayjs(b.startedAt) : undefined;
            if (!aStart && !bStart) {
                return 0;
            } if (!aStart) {
                return 1;
            } if (!bStart) {
                return -1;
            }
            return bStart.valueOf() - aStart.valueOf();
        });
    }, [subjects.list, subjectPerformedList.list]);

    if (subjectPerformedList.changing) {
        return <SubjectPerformedInsUpd
            back={() => {
                setSelectedSubjectPerformed(undefined);
                dispatch(subjectPerformedListSlice.actions.changing(false));
            }}
            schoolId={schoolId}
            classroomId={classroomId}
            subjectPerformed={selectedSubjectPerformed} />;
    }

    if (subjectPerformedList.selectedToPresence) {
        return <PresenceList
            subject={subjects.list?.find((s) => s.id == subjectPerformedList.selectedToPresence?.subjectId)}
            subjectPerformed={subjectPerformedList.selectedToPresence}
            back={() => {
                dispatch(subjectPerformedListSlice.actions.selectedToPresence(undefined));
            }} />;
    }
        

    const buildContent = () => {
        if (list.length == 0) {
            return <Paper sx={{ p: 2, m: 2, width: "100%" }}>
                <Typography variant="h6">Nenhuma aula ministrada</Typography>
                <Typography>Não há registros de aulas ministradas associadas a esta turma.</Typography>
            </Paper>;
        }
        return <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2, width: "100%" }}>
            <Table sx={{ minWidth: 650, width: "100%" }} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ações</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Disciplina</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Início</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Fim</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Horas Realizadas</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody> 
                    {list.map((performed) => {
                        return <TableRow key={performed.id}>
                            <TableCell align="left">
                                {canEdit && <ButtonGroup variant="outlined" size="small">
                                    <Button
                                        color="error"
                                        onClick={() => {
                                            setSubjectPerformedToDelete(performed);
                                        }}>
                                        <DeleteIcon />
                                    </Button>
                                    <Button
                                        color="primary"
                                        onClick={() => {
                                            setSelectedSubjectPerformed(performed);
                                            dispatch(subjectPerformedListSlice.actions.changing(true));
                                        }}>
                                        <EditIcon />
                                    </Button>
                                    <Tooltip title="Fazer conferência de presença (chamada)">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                dispatch(subjectPerformedListSlice.actions.selectedToPresence(performed));
                                            }}>
                                            <FactCheckIcon />
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>}
                            </TableCell>
                            <TableCell align="left">
                                {subjects.list?.find(sub => sub.id == performed.subjectId)?.name || `Disciplina ${performed.subjectId}`}
                            </TableCell>
                            <TableCell align="left">
                                {performed?.startedAt ? dayjs(performed.startedAt).format('DD/MM/YYYY HH:mm') : "-"}
                            </TableCell>
                            <TableCell align="left">
                                {performed?.endedAt ? dayjs(performed.endedAt).format('DD/MM/YYYY HH:mm') : "-"}
                            </TableCell>
                            <TableCell align="left">
                                {performed?.hoursPerformed != null ? `${performed.hoursPerformed}h` : "-"}
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
            <SubjectPerformedDeleteModal
                subjectPerformed={subjectPerformedToDelete}
                onClose={() => setSubjectPerformedToDelete(undefined)}
            />
        </Box>;
    };

    const buttons = canEdit ? [
        {
            label: <AddIcon />,
            click: () => {
                setSelectedSubjectPerformed(undefined);
                dispatch(subjectPerformedListSlice.actions.changing(true));
            }
        }
    ] : undefined;

    return <PrizaContainer
        toolbar={{
            label: "Aulas Ministradas",
            buttons: buttons
        }}
        state={worstState([subjects.state, subjectPerformedList.state])}
        retry={() => {
            dispatch(subjectListSlice.actions.clean());
            dispatch(subjectPerformedListSlice.actions.clean());
        }}
        messages={{
            empty: "Nenhuma aula ministrada associada a esta turma.",
            error: "Erro ao carregar aulas ministradas."
        }}
        content={buildContent}
    />;
};

export default SubjectPerformedList;


